import type { Block, Cell, Inline } from "./blocks.ts";
import { parseInline } from "./inline.ts";

function cellInlines(cell: Cell): Inline[] {
  return typeof cell === "string" ? [cell] : cell.flatMap((x) => (typeof x === "string" ? [x] : []));
}

/** Every inline-markup string in the blocks, in document order. */
function inlines(blocks: Block[]): Inline[] {
  return blocks.flatMap((b): Inline[] => {
    switch (b.type) {
      case "p":
      case "note":
      case "warning":
        return [b.text];
      case "kv":
        return b.pairs.map(([, v]) => v);
      case "bullets":
      case "steps":
        return b.items;
      case "table":
        return [...b.columns, ...b.rows.flat().flatMap(cellInlines)];
      case "groupTable":
        return b.groups.flatMap((g) => g.items.flatMap(cellInlines));
      default:
        return [];
    }
  });
}

function inlinePlain(s: Inline): string {
  return parseInline(s)
    .map((t) => (t.t === "placeholder" ? t.label : t.t === "doc" ? `${t.text} ${t.code}` : t.text))
    .join("");
}

function cellPlain(cell: Cell): string {
  return typeof cell === "string"
    ? inlinePlain(cell)
    : cell.map((x) => (typeof x === "string" ? inlinePlain(x) : x.text)).join("\n");
}

function blockLines(b: Block): string[] {
  switch (b.type) {
    case "h1":
    case "h2":
      return [b.title];
    case "p":
      return [inlinePlain(b.text)];
    case "kv":
      return b.pairs.map(([k, v]) => `${k}: ${inlinePlain(v)}`);
    case "bullets":
    case "steps":
      return b.items.map(inlinePlain);
    case "table":
      return [b.columns.map(inlinePlain).join(" "), ...b.rows.map((row) => row.map(cellPlain).join(" "))];
    case "groupTable":
      return b.groups.flatMap((g) => [[g.title, g.meta].filter(Boolean).join(" "), ...g.items.map(cellPlain)]);
    case "code":
      return [b.text];
    case "note":
    case "warning":
      return [[b.title, inlinePlain(b.text)].filter(Boolean).join(": ")];
    case "pageBreak":
      return [];
    case "image":
      return [[b.caption, b.alt].filter(Boolean).join(" ")];
  }
}

/** Plain text of the blocks for the search index (spec §7): one line per block part, markup removed. */
export function contentText(blocks: Block[]): string {
  return blocks
    .flatMap(blockLines)
    .filter((l) => l !== "")
    .join("\n");
}

const unique = (xs: string[]) => [...new Set(xs)];

/** Labels of [[…]] placeholders still in the document, first occurrence order, without duplicates. */
export function findPlaceholders(blocks: Block[]): string[] {
  return unique(
    inlines(blocks).flatMap((s) => parseInline(s).flatMap((t) => (t.t === "placeholder" ? [t.label] : []))),
  );
}

/** Codes of documents linked with [text](doc:CODE), first occurrence order, without duplicates. */
export function docLinkCodes(blocks: Block[]): string[] {
  return unique(inlines(blocks).flatMap((s) => parseInline(s).flatMap((t) => (t.t === "doc" ? [t.code] : []))));
}
